/**
 * Classic US Millionaire money tree, scalable to any top prize.
 */
import {
  DEFAULT_TOP_PRIZE,
  QUESTION_COUNT,
  SAFE_HAVEN_INDICES
} from "./millionaireConstants";

/** Classic US default ladder (exact show amounts). */
export const CLASSIC_AMOUNTS = [
  100, 200, 300, 500, 1000, 2000, 4000, 8000, 16000, 32000, 64000, 125000, 250000,
  500000, 1000000
];

/** Classic weights relative to $1,000,000 (used when scaling custom tops). */
export const LADDER_WEIGHTS = CLASSIC_AMOUNTS.map((a) => a / DEFAULT_TOP_PRIZE);

/**
 * Round to a "nice" currency step (1/2/5 × 10^n).
 * @param {number} value
 * @param {boolean} allowCents
 */
export function roundNice(value, allowCents) {
  const v = Number(value);
  if (!Number.isFinite(v) || v <= 0) return allowCents ? 0.01 : 1;

  if (!allowCents) {
    if (v < 1) return 1;
    const exp = Math.floor(Math.log10(v));
    const mag = 10 ** exp;
    const norm = v / mag;
    let nice;
    if (norm < 1.5) nice = 1;
    else if (norm < 3) nice = 2;
    else if (norm < 7) nice = 5;
    else nice = 10;
    return Math.max(1, Math.round(nice * mag));
  }

  if (v < 0.01) return 0.01;
  if (v < 1) {
    const cents = Math.round(v * 100);
    const steps = [1, 5, 10, 25, 50];
    let bestStep = 1;
    let best = cents;
    let bestDist = Infinity;
    for (const s of steps) {
      const snapped = Math.max(s, Math.round(cents / s) * s);
      const d = Math.abs(cents - snapped);
      if (d < bestDist) {
        bestDist = d;
        best = snapped;
        bestStep = s;
      }
    }
    void bestStep;
    return Math.max(1, best) / 100;
  }

  return roundNice(v, false);
}

function enforceStrictlyIncreasing(amounts, topPrize, allowCents) {
  const out = [...amounts];
  const minStep = allowCents ? 0.01 : 1;
  out[out.length - 1] = topPrize;

  for (let i = 1; i < out.length; i++) {
    if (out[i] <= out[i - 1]) {
      out[i] = roundNice(out[i - 1] + minStep, allowCents);
    }
  }

  // If we overshot the top while fixing increases, compress middle tiers
  if (out[out.length - 1] !== topPrize || out[out.length - 2] >= topPrize) {
    out[out.length - 1] = topPrize;
    for (let i = out.length - 2; i >= 0; i--) {
      const maxAllowed = topPrize - (out.length - 1 - i) * minStep;
      if (out[i] >= out[i + 1]) {
        out[i] = roundNice(Math.max(minStep, out[i + 1] - minStep), allowCents);
      }
      if (out[i] > maxAllowed) {
        out[i] = roundNice(Math.max(minStep, maxAllowed), allowCents);
      }
    }
    // Forward pass again
    for (let i = 1; i < out.length - 1; i++) {
      if (out[i] <= out[i - 1]) {
        out[i] = roundNice(out[i - 1] + minStep, allowCents);
      }
      if (out[i] >= topPrize) {
        out[i] = roundNice(topPrize - (out.length - 1 - i) * minStep, allowCents);
      }
    }
    out[out.length - 1] = topPrize;
  }

  return out.map((a, i) =>
    i === out.length - 1
      ? topPrize
      : allowCents
        ? Math.round(a * 100) / 100
        : Math.round(a)
  );
}

/**
 * Build a 15-tier money ladder for a given top prize.
 * @param {number} topPrize
 * @returns {{ amounts: number[], safeIndices: number[], topPrize: number }}
 */
export function buildLadder(topPrize = DEFAULT_TOP_PRIZE) {
  let top = Number(topPrize);
  if (!Number.isFinite(top) || top <= 0) top = DEFAULT_TOP_PRIZE;
  // Clamp extreme values
  top = Math.min(top, 1_000_000_000);
  top = Math.max(top, 0.15); // enough room for 15 increasing cent tiers

  // Exact classic show ladder for the default $1,000,000 top prize
  if (top === DEFAULT_TOP_PRIZE) {
    return {
      amounts: [...CLASSIC_AMOUNTS],
      safeIndices: [...SAFE_HAVEN_INDICES],
      topPrize: top
    };
  }

  const allowCents = top < 1000;
  const minFirst = top >= 100 ? 1 : 0.01;

  const raw = LADDER_WEIGHTS.map((w, i) => {
    if (i === QUESTION_COUNT - 1) return top;
    const scaled = top * w;
    return Math.max(minFirst, roundNice(scaled, allowCents));
  });

  const amounts = enforceStrictlyIncreasing(raw, top, allowCents);

  return {
    amounts,
    safeIndices: [...SAFE_HAVEN_INDICES],
    topPrize: top
  };
}

/**
 * Guaranteed payout if the player misses question at `questionIndex` (0-based).
 * @param {number[]} amounts
 * @param {number[]} safeIndices
 * @param {number} questionIndex
 */
export function dropAmount(amounts, safeIndices, questionIndex) {
  const sorted = [...safeIndices].sort((a, b) => a - b);
  let guaranteed = 0;
  for (const idx of sorted) {
    if (questionIndex > idx) {
      guaranteed = amounts[idx];
    }
  }
  return guaranteed;
}

/**
 * Winnings after correctly answering question at `questionIndex`.
 */
export function winningsAfterCorrect(amounts, questionIndex) {
  return amounts[questionIndex] ?? 0;
}

/**
 * Walk-away amount before answering current question (previous tier, or 0).
 */
export function walkAwayAmount(amounts, questionIndex) {
  if (questionIndex <= 0) return 0;
  return amounts[questionIndex - 1] ?? 0;
}
